/**
 * @ Author: Kamil Michalak (k.michalak@kstudio.pl)
 * @ Create Time: 2019-09
 * @ Modified by: Kamil Michalak (k.michalak@kstudio.pl)
 * @ Modified time: 2019-10
 */

import { action, computed, observable, makeObservable, runInAction } from "mobx"
import { ClassAbstract } from "store/ClassTools"
import { MinuteInMs, moment } from "store/util/moment"
import { rootStore } from "store/RootStore"
import { SourceTypeList } from "store/page/player/source/SourceTypeList"
import { channelId, startTs, endTs, durationInSeconds, durationInMinutes, isTimeInEventRange } from "utils/EventHelpers"
import { l10n } from "store/lang/L10n"
//import { TIME_FORMAT_BACKEND } from "store/model/Time"

//MTVW-509
// export const PROGRESSBAR_STEPS = 300
// from material UI documentation: We recommend (max - min) to be evenly divisible by the step.
// MTVW-1079: increase from 400 to 1600 for better centering of live marker on thumb
export const PROGRESSBAR_STEPS = 1600
const liveOffset = 40000 // MTVW-103: offset time of 40 sec to consider live position for progress bar calculations, also used in progressValueBufferInPercents and progressValueLiveMarker calculations

const helperFormatTime = time => time.format("h:*mm:ss", { trunc: true, stopTrim: "*" })
const nDash = '\u2013'

export class PlayerProgressBar extends ClassAbstract {
	positionStep = 0
	positionDone = true
	// MTVW-1079: add progressValueLiveMarkerValue
	progressValueLiveMarkerValue = 0
	currenPositionMoment = 0
	loadingTimer = null
	dragMarkerInfo = {
		dragStartPosition: null,
		contenId: null
	}

	constructor(parent, path) {
		super(parent, path)
		makeObservable(this, {
			positionStep: observable,
			positionDone: observable,
			progressValueLiveMarkerValue: observable,
			dragMarkerInfo: observable,
			currentPlayTimeStamp: computed,
			eventStartTimeStamp: computed,
			durationInMinutes: computed,
			isVisible: computed,
			positionTimeStamp: computed,
			textBefore: computed,
			textAfter: computed,
			progressValue: computed,
			progressValueInPercents: computed,
			progressValueBufferInPercents: computed,
			progressValueLiveMarker: computed,
			progressDragMarker: computed,
			scrollProps: computed,
			setPosition: action,
			setPositionDone: action,
			setState: action,
		})
	}

	/**
	 * @returns {import("store/page/player/source/SourceAbstract").SourceAbstract}
	 */
	get source() {
		// MTVW-444: display values for playerFg while playerBg loading
		if (this._parent.player.__parent.playerFg) return this._parent.player.__parent.playerFg._source
		else return this._parent
	}

	/**
	 * @returns {import("store/page/player/visualon/PlayerVo").PlayerVo}
	 */
	get player() {
		// MTVW-444: display values for playerFg while playerBg loading
		if (this._parent.player.__parent.playerFg) return this._parent.player.__parent.playerFg
		else return this._parent.player
	}

	get currentPlayTimeStamp() {
		if (this.positionDone === false) {
			//console.debug("currentPlayTimeStamp a %s", moment(this.eventStartTimeStamp + (this.durationInMinutes * MinuteInMs * this.positionStep) / PROGRESSBAR_STEPS).valueOf())
			return moment(this.eventStartTimeStamp + (this.durationInMinutes * MinuteInMs * this.positionStep) / PROGRESSBAR_STEPS).valueOf()
		}
		//console.debug("currentPlayTimeStamp b %s", this.player.positionTimeStamp)
		return this.player.positionTimeStamp
	}

	get eventStartTimeStamp() {
		return startTs(this.source?.Event?.event)
		// GT12 decision was to use the epg data (https://wiki.qlgroup.ch/pages/viewpage.action?spaceKey=TVS&title=2022.04.05)
		//console.debug("event sessionStart %s, %o", this.source.sessionStart, startTs(this.source?.Event?.event)
	}

	get durationInMinutes() {
		//console.debug("durationInMinutes", durationInMinutes(this.source?.Event?.event), this.source?.Event?.event)
		// milliseconds
		// MTVW-1101: improved precision at end of program
		//return durationInMinutes(this.source?.Event?.event) * 60 * 1000
		return durationInSeconds(this.source?.Event?.event) * 1000 + 2000
	}

	// GT12: MTVW-460, MTVW-502: Channels MS flags
	get isVisible() {
		// GT12
		//return this.source.Event != null && this.player?.isStreamLoaded === true
		// MTVW-444: display values for playerFg while playerBg loading
		if (this._parent.player.__parent.playerFg) return true
		if (!this.source.Event) return false
		const channel = rootStore.channelsService.getChannel(channelId(this.source?.Event?.event))
		//console.debug("isVisible", this.source.Event, this.source, channel, this.source.type, channelId(this.source?.Event?.event))
		const positionMoment = this.currenPositionMoment !== 0 ? this.currenPositionMoment : endTs(this.source?.Event?.event)
		const allowedSince = this.source.type === SourceTypeList.ContentPlaylist ? 0 :
			-10000 + rootStore.page.MsEpg?.replayOrRecordingAllowedSince(channelId(this.source?.Event?.event), startTs(this.source?.Event?.event), endTs(this.source?.Event?.event), this.source.type === SourceTypeList.ContentPlaylist)
		if (channel?.hasForwardRewind && positionMoment > allowedSince) {
			return this.player?.isStreamLoaded === true
		}
		else if (channel?.hasForwardRewind && positionMoment <= allowedSince) {
			if (this.player.isLoading === false) {
				this.positionMoment = allowedSince
			}
			return false
		}
		else return false
	}

	get positionTimeStamp() {
		return moment(this.currentPlayTimeStamp)
			.diff(this.eventStartTimeStamp)
			.valueOf()
	}

	static savedTextBefore = `${nDash}${nDash}:${nDash}${nDash}`
	get textBefore() {
		//console.debug("liveToPausedTransition", rootStore.page.Player.liveToPausedTransition)
		if (rootStore.page.Player.liveToPausedTransition) return PlayerProgressBar.savedTextBefore
		if (this.player.isStreamLoaded) {
			// MTVW-946: cap displayed play time to duration
			PlayerProgressBar.savedTextBefore = helperFormatTime(moment.duration(this.positionDone === false ?
				(this.durationInMinutes * this.positionStep) / PROGRESSBAR_STEPS :
				Math.min(this.positionTimeStamp, this.durationInMinutes.valueOf())))
			return PlayerProgressBar.savedTextBefore
		} else {
			//MTV-3384 don't show data until the event is started
			return `${nDash}${nDash}:${nDash}${nDash}`
		}
	}

	static savedTextAfter = `${nDash}${nDash}:${nDash}${nDash}`
	get textAfter() {
		if (rootStore.page.Player.liveToPausedTransition) return PlayerProgressBar.savedTextAfter
		if (this.player.isStreamLoaded) {
			//return helperFormatTime(this.durationInMinutes)
			const currentMinute = moment.duration(this.positionDone === false ?
				(this.durationInMinutes * this.positionStep) / PROGRESSBAR_STEPS :
				Math.min(this.positionTimeStamp, this.durationInMinutes.valueOf()))
			PlayerProgressBar.savedTextAfter = l10n.featurePlayerRemainingTime.format(helperFormatTime(moment.duration(this.durationInMinutes.valueOf() - moment(currentMinute.valueOf()))))
			return PlayerProgressBar.savedTextAfter
		} else {
			//MTV-3384 don't show data until the event is started
			return `${nDash}${nDash}:${nDash}${nDash}`
		}
	}

	get progressValue() {
		return Math.max(0, (this.currentPlayTimeStamp - this.eventStartTimeStamp) / this.durationInMinutes ?? 0)
	}

	get progressValueInPercents() {
		return this.getValueInPercents(this.progressValue)
	}

	getValueInPercents(value) {
		return Math.round(value * 100 * 100) / 100
	}

	get progressValueBufferInPercents() {
		//MTV-3478: We do not need the condition below. The value is in any case calculated and will provide proper values.
		// if (this.player.isStreamGrowing !== false) {

		// MTVW-103: Calculate the time difference between the beginning of the event and now
		const timeDiffStartToNow = moment().diff(moment(this.eventStartTimeStamp))

		// MTVW-103: If the time difference between the start
		// of the event and now is greater than the duration of the event, the event is in the past
		// liveOffset is the offset time of 40 sec
		if ((timeDiffStartToNow - liveOffset > this.durationInMinutes)) return 100


		return this.getValueInPercents(
			Math.min(this.player.rangeTimeStampEnd - this.eventStartTimeStamp, this._root.time.getTimeStamp() - liveOffset - this.eventStartTimeStamp) / this.durationInMinutes ?? 0)


		// MTVW-103: The calculations in the code commented below produced an error for events longer than 12h
		// TODO: need to investigate to understand the reason for the error after 12 hours
		// return Math.min(
		// 	Math.max(
		// 		0,
		// 		this.getValueInPercents(
		// 			Math.min(this.player.rangeTimeStampEnd - this.eventStartTimeStamp, this._root.time.getTimeStamp() - liveOffset - this.eventStartTimeStamp) / this.durationInMinutes ?? 0
		// 		)
		// 	),
		// 	100
		// )
		// }
		// return 100
	}

	get progressValueLiveMarker() {
		if (this.__parent?.GetEvent?.event?.contentId !== this.dragMarkerInfo.contentId) {
			//console.debug("PlayerProgressBar", this.dragMarkerInfo.dragStartPosition, this.__parent?.GetEvent?.event?.id, this.__parent?.GetEvent?.event?.contentId)
			runInAction(() => {
				// MTVW-1079: no dragMarker at live position
				//this.dragMarkerInfo.dragStartPosition = moment(this.__parent.player.positionTimeStamp).utc()
				this.dragMarkerInfo.contentId = this.__parent?.GetEvent?.event?.contentId
				//console.debug("PlayerProgressBar", this.dragMarkerInfo.dragStartPosition)
			})
		}
		const timeDiffStartToNow = moment().diff(moment(this.eventStartTimeStamp))

		if ((timeDiffStartToNow - liveOffset > this.durationInMinutes)) return 0

		// MTVW-1079: different handling for live source to avoid thumb vs liveMarker visible pixel shift
		//return Math.round((Math.min(this.player.rangeTimeStampEnd - this.eventStartTimeStamp, moment().utc().valueOf() - liveOffset - this.eventStartTimeStamp) / this.durationInMinutes) * PROGRESSBAR_STEPS)
		if (this.player.isLiveSource) {
			return Math.round((Math.min(this.player.positionTimeStamp - this.eventStartTimeStamp, moment().utc().valueOf() - liveOffset - this.eventStartTimeStamp) / this.durationInMinutes) * PROGRESSBAR_STEPS)
		} else {
			return Math.round((Math.min(this.player.rangeTimeStampEnd - this.eventStartTimeStamp, moment().utc().valueOf() - liveOffset - this.eventStartTimeStamp) / this.durationInMinutes) * PROGRESSBAR_STEPS)
		}
	}

	get progressDragMarker() {
		const offset = this.__parent.type === "ChannelLive" ? 0 : 0
		const value = (Math.min(this.player.rangeTimeStampEnd - this.eventStartTimeStamp, this.dragMarkerInfo.dragStartPosition - offset - this.eventStartTimeStamp) / this.durationInMinutes) * PROGRESSBAR_STEPS
		//const value = (moment(this.dragMarkerInfo.dragStartPosition).utc().valueOf() - moment(this.eventStartTimeStamp).utc().valueOf()) / this.durationInMinutes * PROGRESSBAR_STEPS
		//console.debug("PlayerProgressBar progressDragMarker", value, this.dragMarkerInfo.dragStartPosition, this.eventStartTimeStamp)
		if (value === 0) console.debug("progressDragMarker", value)
		return value
	}

	static pos = null
	get scrollProps() {
		let props = {
			max: PROGRESSBAR_STEPS,
			min: 0,
			step: 0.25
		}
		if (!rootStore.page.Player.liveToPausedTransition) {
			if (!isNaN(this.progressValue)) {
				if (this.positionDone === false) {
					PlayerProgressBar.pos = this.positionStep
				} else {
					PlayerProgressBar.pos = Math.round(this.progressValue * PROGRESSBAR_STEPS)
				}
			} else {
				PlayerProgressBar.pos = 0
			}
		}
		props.value = PlayerProgressBar.pos

		// MTVW-1079: calculate progressValueLiveMarkerValue
		runInAction(() => {
			// MTVW-1084: validate live marker with time range
			if (isTimeInEventRange(moment().utc().valueOf(), this.source.Event)) {
				// MTVW-1079: +1 to avoid overlap / non display of live marker, compensated in playerControlProgressBarLiveMark
				this.progressValueLiveMarkerValue = this.progressValueLiveMarker + 1
			} else {
				this.progressValueLiveMarkerValue = null
			}
		})
		//console.debug("scrollProps", props.value, this.progressValueLiveMarkerValue, Math.abs(props.value - this.progressValueLiveMarkerValue))
		return props
	}

	setPosition(positionStep, positionDone = false, thumbTs = null) {
		console.log("@(positionStep=%o,positionDone=%o) .positionDone=%o", positionStep, positionDone, this.positionDone)
		// MTVW-444, MTVW-788, MTVW-787: choose source
		const cSource = this._parent.player.__parent.playerFg ? this._parent.player._source : this.source
		if (!cSource) return

		// GT12: MTVW-460, MTVW-502: Channels MS flags
		const allowedSince = cSource.type === "ContentPlaylist" ? 0 :
		/*-10000 +*/ rootStore.page.MsEpg?.replayOrRecordingAllowedSince(channelId(cSource.Event), startTs(cSource.Event), endTs(cSource.Event), cSource.type === "ContentPlaylist")
		//console.debug("setPosition", moment(this.eventStartTimeStamp + (this.durationInMinutes * positionStep) / PROGRESSBAR_STEPS).utc().format(TIME_FORMAT_BACKEND), moment(allowedSince).utc().format(TIME_FORMAT_BACKEND))
		const step = 0.25
		this.positionStep = !thumbTs ? positionStep : Math.round((((thumbTs - this.eventStartTimeStamp) * PROGRESSBAR_STEPS) / this.durationInMinutes) / step) * step
		if (moment(this.eventStartTimeStamp + (this.durationInMinutes * positionStep) / PROGRESSBAR_STEPS).utc().valueOf() <= allowedSince) {
			this.positionStep = (allowedSince - this.eventStartTimeStamp) * PROGRESSBAR_STEPS / this.durationInMinutes
		}
		//console.debug("setPosition", positionStep, this.positionStep, allowedSince, moment(this.eventStartTimeStamp + (this.durationInMinutes * this.positionStep) / PROGRESSBAR_STEPS).utc().format(), moment(allowedSince).utc().format())
		this.setPositionDone(false, null)
		this.currenPositionMoment = thumbTs ? moment(thumbTs) : moment(this.eventStartTimeStamp + (this.durationInMinutes * this.positionStep) / PROGRESSBAR_STEPS)
		//console.debug("currentPositionMoment", this.currenPositionMoment.utc())
		if (positionDone) {
			//console.debug("handleSetPosition positionStep", this.player.isLoading, this.positionStep)
			const positionMoment = thumbTs ? moment(thumbTs) : moment(this.eventStartTimeStamp + (this.durationInMinutes * this.positionStep) / PROGRESSBAR_STEPS)

			//console.debug("PlayerProgressBar", cSource)
			if (this.player.isLoading === false) {
				cSource.handleSetPosition(positionMoment).then(() => {
					//console.debug("handleSetPosition done!")
					// TODO: handleSetPosition might return prematurely in case of setPositionTimeStamp for replay streams
				}).catch((err) => {
					console.error("PlayerProgressBar setPosition", err)
				}).finally(() => {
					this.setPositionDone(true, positionMoment)
					//console.debug("PlayerProgressBar finally")
				})
			}
			else {
				// MTVW-665: it happened if skip 10s is done multiple times followed by a drag of the slider while the player is still loading
				if (!this.loadingTimer) this.loadingTimer = setInterval(() => {
					if (this.player.isLoading === false) {
						console.debug("PlayerProgressBar handleSetPosition")
						cSource.handleSetPosition(positionMoment).then(() => {
							//console.debug("HIT loadingTimer")
							console.debug("PlayerProgressBar then")
						}).catch((err) => {
							console.error("PlayerProgressBar setPosition", err)
						}).finally(() => {
							this.setPositionDone(true, positionMoment)
							clearInterval(this.loadingTimer)
							this.loadingTimer = null
							//console.debug("PlayerProgressBar finally")
						})
					}
				}, 1000)
			}
		} else {
			//if (!this.positionDone) this.dragStartPosition = this.currenPositionMoment
			this.setPositionDone(false, null)
		}
		return false
	}

	setPositionDone(value, positionMoment) {
		try {
			// end drag
			if (value && positionMoment) {
				//console.debug("endDrag", this.dragMarkerInfo.dragStartPosition)
				this.dragMarkerInfo.dragStartPosition = null
			}
			// start drag
			else {
				if (!this.dragMarkerInfo.dragStartPosition) {
					this.dragMarkerInfo.dragStartPosition = moment(this.__parent.player.positionTimeStamp).utc()
				}
			}
		}
		catch (e) {
			console.error("setPositionDone", e, positionMoment)
		}

		this.positionDone = value
	}

	getTimeFormattedFromDuration() {
		//time => time.format("h:*mm:ss", { trunc: true, stopTrim: "*" })
	}

	getState() {
		return {
			positionStep: this.positionStep,
			positionDone: this.positionDone
		}
	}

	setState(state) {
		Object.keys(state)?.forEach(k => {
			this[k] = state[k]
		})
		return this
	}
}
